/**
 * Scripts de terceros que solo se cargan si están configurados y cuando se
 * necesitan: Paddle.js (checkout embebido, SDD §14.5) y Cloudflare Turnstile
 * (captcha del registro por correo, ADR-20).
 */
const env = import.meta.env

export const PADDLE_CLIENT_TOKEN = env.VITE_PADDLE_CLIENT_TOKEN ?? ''
export const PADDLE_ENV: 'sandbox' | 'production' =
  env.VITE_PADDLE_ENV === 'production' ? 'production' : 'sandbox'
export const TURNSTILE_SITE_KEY = env.VITE_TURNSTILE_SITE_KEY ?? ''
/** Sitio público, donde viven los textos legales (apps/landing). */
export const SITIO_URL = env.VITE_SITE_URL ?? 'https://holaeco.cl'

const loading = new Map<string, Promise<void>>()

export function loadScript(src: string): Promise<void> {
  const existing = loading.get(src)
  if (existing) return existing
  const p = new Promise<void>((resolve, reject) => {
    const s = document.createElement('script')
    s.src = src
    s.async = true
    s.onload = () => resolve()
    s.onerror = () => {
      loading.delete(src)
      reject(new Error(`No se pudo cargar ${new URL(src).host}`))
    }
    document.head.appendChild(s)
  })
  loading.set(src, p)
  return p
}

// ---------------------------------------------------------------------------
// Paddle.js v2
// ---------------------------------------------------------------------------
interface PaddleGlobal {
  Environment: { set(env: 'sandbox'): void }
  Initialize(opts: { token: string; eventCallback?: (ev: { name?: string }) => void }): void
  Checkout: { open(opts: { transactionId: string; settings?: Record<string, unknown> }): void }
}
declare global {
  interface Window {
    Paddle?: PaddleGlobal
    turnstile?: {
      render(el: HTMLElement, opts: Record<string, unknown>): string
      reset(id?: string): void
      remove(id?: string): void
    }
  }
}

let paddleReady: Promise<PaddleGlobal> | null = null
let onCompleted: (() => void) | null = null

/** Abre el checkout embebido con la transacción creada por la API (org_id del servidor). */
export async function openPaddleCheckout(
  transactionId: string,
  completed: () => void
): Promise<void> {
  onCompleted = completed
  paddleReady ??= loadScript('https://cdn.paddle.com/paddle/v2/paddle.js').then(() => {
    const P = window.Paddle
    if (!P) throw new Error('Paddle no cargó')
    if (PADDLE_ENV === 'sandbox') P.Environment.set('sandbox')
    P.Initialize({
      token: PADDLE_CLIENT_TOKEN,
      eventCallback: (ev) => {
        if (ev.name === 'checkout.completed') onCompleted?.()
      }
    })
    return P
  })
  const P = await paddleReady
  P.Checkout.open({
    transactionId,
    settings: { locale: 'es', displayMode: 'overlay', theme: 'dark' }
  })
}

// ---------------------------------------------------------------------------
// Turnstile
// ---------------------------------------------------------------------------
/** Pinta el widget en `el` y devuelve una función para limpiarlo. */
export async function renderTurnstile(
  el: HTMLElement,
  onToken: (t: string | null) => void
): Promise<() => void> {
  await loadScript('https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit')
  const t = window.turnstile
  if (!t) throw new Error('Turnstile no cargó')
  const id = t.render(el, {
    sitekey: TURNSTILE_SITE_KEY,
    language: 'es',
    callback: (token: string) => onToken(token),
    'expired-callback': () => onToken(null),
    'error-callback': () => onToken(null)
  })
  return () => t.remove(id)
}
